import { COUPON_TYPE_LABELS, formatCurrency, formatDate, toDateInput } from '@crm/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  Label,
  Modal,
  Select,
  Spinner,
} from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronRight, Pencil, Plus, Ticket, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  ConfirmDialog,
  EmptyState,
  FilterBar,
  FormField,
  PageHeader,
  Pagination,
} from '@/components';
import { useDebounce, useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { type Coupon, useCoupons, useCreateCoupon, useDeleteCoupon, useUpdateCoupon } from '../api';

const FORM_ID = 'coupon-form';

const couponFormSchema = z.object({
  code: z.string().min(3, 'Codigo muito curto').max(24),
  description: z.string().max(160),
  type: z.enum(['PERCENT', 'AMOUNT']),
  value: z.coerce.number().positive('Informe o desconto'),
  minAmount: z.coerce.number().nonnegative(),
  maxDiscount: z.coerce.number().nonnegative(),
  startsAt: z.string(),
  endsAt: z.string(),
  usageLimit: z.coerce.number().int().nonnegative(),
  perCustomerLimit: z.coerce.number().int().nonnegative(),
  isActive: z.boolean(),
});

type CouponFormValues = z.infer<typeof couponFormSchema>;

const EMPTY: CouponFormValues = {
  code: '',
  description: '',
  type: 'PERCENT',
  value: 10,
  minAmount: 0,
  maxDiscount: 0,
  startsAt: '',
  endsAt: '',
  usageLimit: 0,
  perCustomerLimit: 0,
  isActive: true,
};

/** A regra do cupom em uma frase, para a lista nao virar tabela. */
function describe(coupon: Coupon): string {
  const parts = [
    coupon.type === 'PERCENT'
      ? `${coupon.value}% de desconto`
      : `${formatCurrency(coupon.value)} de desconto`,
  ];

  if (coupon.maxDiscount) parts.push(`ate ${formatCurrency(coupon.maxDiscount)}`);
  if (coupon.minAmount) parts.push(`compras a partir de ${formatCurrency(coupon.minAmount)}`);
  if (coupon.endsAt) parts.push(`vale ate ${formatDate(coupon.endsAt)}`);
  if (coupon.usageLimit) parts.push(`${coupon.usageCount}/${coupon.usageLimit} usos`);
  else if (coupon.usageCount > 0) parts.push(`${coupon.usageCount} usos`);

  return parts.join(' - ');
}

/**
 * O mesmo cupom em uma linha so: no celular a regra inteira ocupava duas
 * linhas de cinza e afogava o codigo, que e o que se procura na lista.
 */
function describeShort(coupon: Coupon): string {
  const desconto =
    coupon.type === 'PERCENT'
      ? `${coupon.value}% de desconto`
      : `${formatCurrency(coupon.value)} de desconto`;

  if (coupon.usageLimit) return `${desconto} - ${coupon.usageCount}/${coupon.usageLimit} usos`;
  return coupon.endsAt ? `${desconto} - ate ${formatDate(coupon.endsAt)}` : desconto;
}

export function CouponsPage() {
  useDocumentTitle('Cupons');

  const canWrite = useCan('finance', 'WRITE');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const debouncedSearch = useDebounce(search, 300);

  const coupons = useCoupons({ page, perPage, status, search: debouncedSearch || undefined });
  const createCoupon = useCreateCoupon();
  const updateCoupon = useUpdateCoupon();
  const deleteCoupon = useDeleteCoupon();

  const [editing, setEditing] = useState<Coupon | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<Coupon | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CouponFormValues>({
    resolver: zodResolver(couponFormSchema),
    defaultValues: EMPTY,
  });

  const type = watch('type');

  useEffect(() => {
    if (!isFormOpen) return;

    reset(
      editing
        ? {
            code: editing.code,
            description: editing.description ?? '',
            type: editing.type,
            value: editing.value,
            minAmount: editing.minAmount ?? 0,
            maxDiscount: editing.maxDiscount ?? 0,
            startsAt: toDateInput(editing.startsAt),
            endsAt: toDateInput(editing.endsAt),
            usageLimit: editing.usageLimit ?? 0,
            perCustomerLimit: editing.perCustomerLimit ?? 0,
            isActive: editing.isActive,
          }
        : EMPTY,
    );
  }, [isFormOpen, editing, reset]);

  function openForm(coupon: Coupon | null) {
    setEditing(coupon);
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditing(null);
  }

  function onSubmit(values: CouponFormValues) {
    // Zero nos limites significa "sem limite"; a API guarda nulo.
    const input = {
      code: values.code,
      description: values.description || undefined,
      type: values.type,
      value: values.value,
      minAmount: values.minAmount || undefined,
      maxDiscount: values.type === 'PERCENT' ? values.maxDiscount || undefined : undefined,
      startsAt: values.startsAt ? new Date(`${values.startsAt}T00:00:00`) : undefined,
      endsAt: values.endsAt ? new Date(`${values.endsAt}T00:00:00`) : undefined,
      usageLimit: values.usageLimit || undefined,
      perCustomerLimit: values.perCustomerLimit || undefined,
      serviceIds: [],
      isActive: values.isActive,
    };

    if (editing) {
      updateCoupon.mutate({ id: editing.id, input }, { onSuccess: closeForm });
      return;
    }

    createCoupon.mutate(input, { onSuccess: closeForm });
  }

  const mutationError = createCoupon.error ?? updateCoupon.error ?? deleteCoupon.error;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Ticket />}
        title="Cupons"
        description="Codigos de desconto que o cliente usa na hora de pagar."
        actions={
          canWrite ? (
            <Button onClick={() => openForm(null)}>
              <Plus className="h-4 w-4" />
              <span>
                Novo<span className="hidden sm:inline"> cupom</span>
              </span>
            </Button>
          ) : null
        }
      />

      {mutationError ? (
        <p className="text-sm text-destructive">{toErrorMessage(mutationError)}</p>
      ) : null}

      <Card variant="flush">
        <CardContent className="space-y-6">
          <FilterBar
            className="sm:grid-cols-2"
            activeCount={status !== 'all' ? 1 : 0}
            search={
              <Input
                placeholder="Buscar por codigo"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            }
          >
            <Select
              aria-label="Situacao"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as 'all' | 'active' | 'inactive');
                setPage(1);
              }}
            >
              <option value="all">Todos</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </Select>
          </FilterBar>

          {coupons.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : coupons.data && coupons.data.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {coupons.data.data.map((coupon) => (
                <li key={coupon.id} className="flex items-center gap-2 py-3 first:pt-0 last:pb-0">
                  {/* A linha toda abre o cupom: no celular o alvo e o registro
                      inteiro, nao um icone no canto. */}
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => openForm(coupon)}
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-mono font-medium tracking-wide">{coupon.code}</span>
                      <Badge variant="secondary">{COUPON_TYPE_LABELS[coupon.type]}</Badge>
                      {coupon.isExpired ? <Badge variant="destructive">Esgotado</Badge> : null}
                      {coupon.isActive ? null : <Badge variant="outline">Inativo</Badge>}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground sm:hidden">
                      {describeShort(coupon)}
                    </span>
                    {/* Duas linhas: a regra inteira do cupom nao cabe em uma. */}
                    <span className="line-clamp-2 hidden text-xs text-muted-foreground sm:block">
                      {coupon.description ? `${coupon.description} - ` : ''}
                      {describe(coupon)}
                    </span>
                  </button>

                  <ChevronRight
                    aria-hidden
                    className="h-5 w-5 shrink-0 text-muted-foreground sm:hidden"
                  />

                  {canWrite ? (
                    <div className="hidden shrink-0 items-center sm:flex sm:justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Editar"
                        onClick={() => openForm(coupon)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Excluir"
                        onClick={() => setPendingRemoval(coupon)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<Ticket />} title="Nenhum cupom cadastrado" />
          )}

          <Pagination
            meta={coupons.data?.meta}
            label="cupons"
            onChange={setPage}
            perPage={perPage}
            onPerPageChange={(value) => {
              setPerPage(value);
              setPage(1);
            }}
          />
        </CardContent>
      </Card>

      <Modal
        open={isFormOpen}
        onClose={closeForm}
        title={editing ? 'Editar cupom' : 'Novo cupom'}
        subtitle="Zero em qualquer limite significa sem limite."
        size="medium"
        // Excluir mora no proprio cadastro: e o que dispensa a fileira de
        // botoes em cada linha da lista no celular.
        actions={[
          editing
            ? {
                label: 'Excluir',
                icon: <Trash2 />,
                variant: 'ghost' as const,
                onClick: () => {
                  setPendingRemoval(editing);
                  closeForm();
                },
              }
            : { label: 'Cancelar', variant: 'ghost' as const, onClick: closeForm },
          {
            label: editing ? 'Salvar' : 'Criar',
            type: 'submit' as const,
            formId: FORM_ID,
            loading: createCoupon.isPending || updateCoupon.isPending,
          },
        ]}
      >
        <form id={FORM_ID} className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              label="Codigo"
              placeholder="BEMVINDO10"
              className="font-mono uppercase"
              error={errors.code?.message}
              {...register('code')}
            />

            <div className="space-y-2">
              <Label htmlFor="type">Tipo</Label>
              <Select id="type" {...register('type')}>
                <option value="PERCENT">{COUPON_TYPE_LABELS.PERCENT}</option>
                <option value="AMOUNT">{COUPON_TYPE_LABELS.AMOUNT}</option>
              </Select>
            </div>
          </div>

          <FormField
            label="Descricao"
            placeholder="Primeira visita"
            error={errors.description?.message}
            {...register('description')}
          />

          <div className="grid gap-4 sm:grid-cols-3">
            <FormField
              label={type === 'PERCENT' ? 'Desconto (%)' : 'Desconto (R$)'}
              type="number"
              step="0.01"
              min="0"
              error={errors.value?.message}
              {...register('value')}
            />
            <FormField
              label="Compra minima (R$)"
              type="number"
              step="0.01"
              min="0"
              error={errors.minAmount?.message}
              {...register('minAmount')}
            />
            {type === 'PERCENT' ? (
              <FormField
                label="Teto do desconto (R$)"
                type="number"
                step="0.01"
                min="0"
                error={errors.maxDiscount?.message}
                {...register('maxDiscount')}
              />
            ) : null}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              label="Comeca em"
              type="date"
              error={errors.startsAt?.message}
              {...register('startsAt')}
            />
            <FormField
              label="Vale ate"
              type="date"
              error={errors.endsAt?.message}
              {...register('endsAt')}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              label="Limite de usos"
              type="number"
              min="0"
              error={errors.usageLimit?.message}
              {...register('usageLimit')}
            />
            <FormField
              label="Usos por cliente"
              type="number"
              min="0"
              error={errors.perCustomerLimit?.message}
              {...register('perCustomerLimit')}
            />
          </div>

          <Checkbox label="Cupom ativo" {...register('isActive')} />
        </form>
      </Modal>

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Excluir cupom"
        message={`O cupom ${pendingRemoval?.code ?? ''} para de valer. Os usos ja registrados ficam no historico.`}
        loading={deleteCoupon.isPending}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) deleteCoupon.mutate(pendingRemoval.id);
          setPendingRemoval(null);
        }}
      />
    </div>
  );
}
